import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  forwardRef,
  Input,
  OnDestroy,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
} from '@angular/forms';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import {
  FileInfo,
  FileRestrictions,
  FileSelectComponent as KendoFileSelectComponent,
  SelectEvent,
} from '@progress/kendo-angular-upload';
import { DropDownButtonComponent, PopupSettings } from '@progress/kendo-angular-buttons';
import { finalize, take } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import * as _ from 'lodash';

import { Attach, Attaches } from '@app/shared/models/attach';
import { IMAGE_EXTENSIONS, PDF_EXTENSION } from '@app/shared/services/attach.service';
import { DialogService } from '@technocom/ui/dialogs';

const getFileExtension = (fileName: string): string => {
  const rFileExtension = /\.([^\.]+)$/;
  const matches: RegExpMatchArray = fileName?.match(rFileExtension);
  return matches ? matches[0] : '';
};

type Settings = Array<{ text: string; icon?: string; handler?: (dataItem?: FileInfo, ...args: any[]) => void }>;

@Component({
  selector: 'app-file-select',
  templateUrl: './file-select.component.html',
  styleUrls: ['./file-select.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => FileSelectComponent), multi: true },
    { provide: NG_VALIDATORS, useExisting: forwardRef(() => FileSelectComponent), multi: true },
  ],
})
export class FileSelectComponent implements OnDestroy, ControlValueAccessor, Validator {
  protected _onChange: (attaches: Attaches) => void;
  protected _onTouched: () => void;
  protected subscription: Subscription;
  protected _attachFileInfoMap: Map<FileInfo, Attach>; // связь между Attach и FileInfo
  files: FileInfo[];
  disabled: boolean;
  editableFile: FileInfo;
  settings: Settings;
  selectedFile: FileInfo;

  @Input() downloadUrl: string;
  @Input() withPreview = true;
  @Input() multiple: boolean;
  @Input() restrictions: FileRestrictions;
  @Input() canEdit: boolean; // может ли пользователь редактировать существующие файлы?

  @ViewChild(KendoFileSelectComponent) kendoFileSelectCmp: KendoFileSelectComponent;

  @ViewChild('input') set input(elRef: ElementRef<HTMLInputElement>) {
    setTimeout(() => elRef?.nativeElement.focus());
  }

  @ViewChildren('dropDownBtn') dropDownBtns: QueryList<DropDownButtonComponent>;

  constructor(protected cd: ChangeDetectorRef, protected dialogService: DialogService) {
    this.canEdit = true;
    this.multiple = true;
    this._onChange = _.noop;
    this._onTouched = _.noop;
    this.subscription = new Subscription();
    this.files = [];
    this._attachFileInfoMap = new Map();
    this.editFile = this.editFile.bind(this);
    this.removeFile = this.removeFile.bind(this);
    this.isNewAttach = this.isNewAttach.bind(this);
    this.settings = [
      {
        text: 'Переименовать',
        icon: 'edit',
        handler: this.editFile,
      },
      {
        text: 'Удалить',
        icon: 'delete',
        handler: this.removeFile,
      },
    ];
  }

  ngOnDestroy(): void {
    const { attaches } = this;
    for (const attach of attaches) {
      if (attach.docUrl?.startsWith('blob:')) {
        URL.revokeObjectURL(attach.docUrl);
        delete attach.docUrl;
      }
    }
    this._attachFileInfoMap.clear();
  }

  protected get attaches(): Attaches {
    return Array.from(this._attachFileInfoMap.values());
  }

  get settingsBtnPopupOptions(): PopupSettings {
    /**
     * указываем appendTo: 'root', чтобы можно было скрыть popup окно,
     * когда для .file-box`a происходит событие cdkDragStarted.
     * Если указать 'component', то перетаскиваемый элемент целиком клонируется
     * (вместе с dropdown кнопкой) и вставляется перед </body>.
     * Управлять отображением popup окна не представляется возможным.
     */
    return { animate: true, popupClass: 'file-box__settings-btn', appendTo: 'root' };
  }

  get selectedAttach(): Attach {
    return this.getAttachByFile(this.selectedFile);
  }

  isSelectedFile(file: FileInfo): boolean {
    return file === this.selectedFile;
  }

  /**
   * Кнопка доступна, если form control is enabled
   * и у пользователя есть право на редактирование файлов или это новый файл
   */
  isSettingsBtnVisible(file: FileInfo): boolean {
    const attach = this.getAttachByFile(file);
    if (this.disabled) {
      return false;
    }
    return this.canEdit || this.isNewAttach(attach);
  }

  selectFile(file: FileInfo) {
    if (this.isSelectedFile(file) === false) {
      this.selectedFile = file;
    }
  }

  registerOnTouched(fn: any): void {
    this._onTouched = fn;
  }

  registerOnChange(fn: any): void {
    this._onChange = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.cd.markForCheck();
  }

  writeValue(attaches: Attaches | Attach): void {
    if (_.isNil(attaches)) {
      return;
    }
    if (!Array.isArray(attaches)) {
      attaches = [attaches];
    }
    attaches = attaches.filter(i => !!i);
    this._attachFileInfoMap.clear();
    for (let attach of attaches) {
      // запишем в docUrl ссылку на файл
      if (this.isNewAttach(attach)) {
        attach = { ..._.cloneDeep(attach), docUrl: this.createObjectUrlForAttach(attach) };
      } else {
        attach = { ..._.cloneDeep(attach), docUrl: attach.docUrl ?? `${this.downloadUrl}/${attach.id}` };
      }
    }
    const selectedIndex = this.files.indexOf(this.selectedFile);
    this.files = _.sortBy(attaches, 'docNum').map((attach: Attach) => {
      const fileInfo = attach.fileInfo || {
        name: attach.docName,
        extension: getFileExtension(attach.fileName),
        size: attach.fileSize,
      };
      attach = { ..._.cloneDeep(attach), fileInfo };
      this._attachFileInfoMap.set(fileInfo, attach);
      return fileInfo;
    });
    // восстанавливаем выбранный файл по индексу
    this.selectedFile = this.files[selectedIndex];
    this.selectFirstFile();
    this.cd.markForCheck();
  }

  validate(control: AbstractControl): ValidationErrors | null {
    const noErrors = this.files.every((file: FileInfo) => _.isEmpty(file.validationErrors));
    return noErrors ? null : { attach: true };
  }

  drop(event: CdkDragDrop<Attaches>) {
    if (this.disabled) {
      return;
    }
    moveItemInArray(this.files, event.previousIndex, event.currentIndex);
    this.updateDocNums();
  }

  removeFile(file: FileInfo) {
    this.subscription.add(
      this.dialogService
        .confirm({
          title: 'Внимание!',
          content: 'Вы точно хотите удалить запись?',
        })
        .pipe(finalize(() => this.cd.markForCheck()))
        .subscribe(() => {
          const attach = this.getAttachByFile(file);
          if (this.isNewAttach(attach)) {
            URL.revokeObjectURL(attach.docUrl);
          }
          this._attachFileInfoMap.delete(file);
          this.files = _.without(this.files, file);
          this.updateDocNums();
          if (this.isSelectedFile(file)) {
            this.selectedFile = null;
            this.selectFirstFile();
          }
        }),
    );
  }

  onSelectFiles(event: SelectEvent) {
    event.preventDefault();
    for (const file of event.files) {
      const attach: Attach = {
        id: null,
        fileName: file.name,
        fileSize: file.size,
        docName: file.name,
        docNum: this.files.length + 1,
        fileInfo: file,
      };
      attach.docUrl = this.createObjectUrlForAttach(attach);
      this._attachFileInfoMap.set(file, attach);
      this.files = [...this.files, file];
    }
    this.selectFirstFile();
    this.notifyChange();
  }

  /**
   * из UI в control
   */
  protected notifyChange() {
    this._onChange(this.attaches);
  }

  /**
   * обновляем docNum у attach. После drag`n drop или удаления
   */
  protected updateDocNums() {
    this.files.forEach((file: FileInfo, i: number) => {
      this._attachFileInfoMap.set(file, { ...this._attachFileInfoMap.get(file), docNum: i + 1 });
    });
    this.notifyChange();
  }

  /**
   * Обработчик клика по имени файла. Переводит запись в режим редактирования.
   */
  editFile(file: FileInfo, dropDownBtn: DropDownButtonComponent) {
    if (this.disabled) {
      return;
    }
    // после скрытия popup окна установим focus на input
    dropDownBtn.close.pipe(take(1)).subscribe(() => {
      this.editableFile = file;
      this.cd.markForCheck();
    });
  }

  /**
   * запись в режиме редактирования?
   */
  isEditable(file: FileInfo): boolean {
    return this.editableFile === file;
  }

  /**
   * ни один из файлов не в режиме редактирования
   */
  clearEditableState(newName: string) {
    const attach = this.getAttachByFile(this.editableFile);
    if (attach) {
      attach.docName = newName || attach.docName;
      this.editableFile.name = attach.docName;
    }
    this.editableFile = null;
    this.notifyChange();
  }

  getAttachByFile(file: FileInfo): Attach {
    return this._attachFileInfoMap.get(file);
  }

  /**
   * Выбирает первый файл для отображения из массива files
   */
  protected selectFirstFile() {
    const first = _.first(this.files);
    if (_.isNil(this.selectedFile) && first) {
      this.selectFile(first);
    }
  }

  protected isNewAttach(attach: Attach): boolean {
    return _.isNil(attach.id);
  }

  protected createObjectUrlForAttach(attach: Attach): string {
    if ([...IMAGE_EXTENSIONS, PDF_EXTENSION].includes(attach.fileInfo.extension.toLowerCase())) {
      // создаем внутреннюю ссылку на файл для image-like / pdf-like документов
      return URL.createObjectURL(attach.fileInfo.rawFile);
    }
    return null;
  }

  onDropDownClick(event: Event, dropDownCmp: DropDownButtonComponent) {
    // скрываем другие открытые меню
    this.dropDownBtns.filter(cmp => cmp !== dropDownCmp && cmp.isOpen).forEach(cmp => cmp.toggle(false));
    event.stopPropagation();
  }

  onDragStarted() {
    // скрываем меню у элементов
    this.dropDownBtns.forEach(cmp => cmp.toggle(false));
  }
}
